import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, ArrowRight } from 'lucide-react';
import { API_URL } from '../config';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Handle Login
        const formData = new URLSearchParams();
        formData.append('username', email);
        formData.append('password', password);

        const response = await fetch(`${API_URL}/api/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString()
        });

        const data = await response.json();

        if (response.ok) {
          localStorage.setItem('token', data.access_token);
          
          // Check if admin
          const userRes = await fetch(`${API_URL}/api/users/me`, {
            headers: { 'Authorization': `Bearer ${data.access_token}` }
          });
          const userData = await userRes.json();
          
          localStorage.setItem('is_admin', userData.is_admin);

          if (userData.is_admin) {
            window.location.href = '/admin';
          } else {
            window.location.href = '/';
          }
        } else {
          setError(data.detail || 'Login failed');
        }
      } else {
        // Handle Sign Up
        const response = await fetch(`${API_URL}/api/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        
        const data = await response.json();

        if (response.ok) {
          // Success, switch to login view
          setIsLogin(true);
          setPassword('');
          setConfirmPassword('');
          setError('Account created successfully! Please sign in.');
        } else {
          setError(data.detail || 'Signup failed');
        }
      }
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      backgroundColor: '#f9fafb',
      padding: '2rem'
    }}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '16px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ 
            backgroundColor: '#ea580c', 
            width: '60px', height: '60px', 
            borderRadius: '50%', 
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            margin: '0 auto 1rem auto'
          }}>
            <Lock color="white" size={30} />
          </div>
          <p style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 800, color: '#ea580c', letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: 'var(--font-sans)' }}>Adventure</p>
          <h2 style={{ margin: 0, color: '#111', fontSize: '1.8rem' }}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            {isLogin ? 'Sign in to continue' : 'Register to start your journey'}
          </p>
        </div>

        {error && (
          <div style={{ 
            backgroundColor: error.includes('successfully') ? '#dcfce7' : '#fee2e2', 
            color: error.includes('successfully') ? '#166534' : '#991b1b', 
            padding: '1rem', borderRadius: '8px', 
            marginBottom: '1.5rem', fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '10px', left: '12px', color: '#9ca3af' }}>
                <Mail size={20} />
              </div>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ 
                  width: '100%', padding: '10px 10px 10px 40px', 
                  borderRadius: '8px', border: '1px solid #d1d5db',
                  fontSize: '1rem', outline: 'none'
                }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: isLogin ? '2rem' : '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '10px', left: '12px', color: '#9ca3af' }}>
                <Lock size={20} />
              </div>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ 
                  width: '100%', padding: '10px 10px 10px 40px', 
                  borderRadius: '8px', border: '1px solid #d1d5db',
                  fontSize: '1rem', outline: 'none'
                }}
                required
              />
            </div>
          </div>

          <AnimatePresence>
            {!isLogin && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ marginBottom: '2rem', overflow: 'hidden' }}
              >
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '10px', left: '12px', color: '#9ca3af' }}>
                    <Lock size={20} />
                  </div>
                  <input 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ 
                      width: '100%', padding: '10px 10px 10px 40px', 
                      borderRadius: '8px', border: '1px solid #d1d5db',
                      fontSize: '1rem', outline: 'none'
                    }}
                    required={!isLogin}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Sign Up')}
            {!loading && <ArrowRight size={18} />}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button 
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setPassword('');
              setConfirmPassword('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#ea580c',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
